import type { CaseSummary } from './cors-provenance-api';
import { getCaseInternalName } from './functions';

/**
 * Look up a case from ``useCasesList`` by whatever the nav currently holds.
 *
 * The dropdown value is the display ``caseName``. Provenance / evidence /
 * timeline filters must send the list's ``caseTag`` (``case:<uuid>`` or
 * ``case:<name>``) — never a locally derived ``case:${name}``. Matching also
 * accepts a tag or tag body so a URL or leftover selection still resolves
 * (TR-359).
 *
 * ``*`` / All Cases / empty are not a case: callers omit the tags filter.
 */
/**
 * True for the cross-case selection (``*`` / All Cases), which is not a case:
 * it has no ``caseTag``, so anything that needs one has to ask for a single
 * case rather than treat the absent tag as a lookup failure.
 */
export function isAllCasesSelection(selected: string | null | undefined): boolean {
  const trimmed = selected?.trim() ?? '';
  return trimmed === '*' || trimmed.toLowerCase() === 'all cases';
}

export function findCaseSummary(
  cases: CaseSummary[] | undefined | null,
  selected: string | null | undefined,
): CaseSummary | undefined {
  if (!cases?.length || selected == null) return undefined;
  const trimmed = selected.trim();
  if (!trimmed || isAllCasesSelection(trimmed)) {
    return undefined;
  }

  const normalised = trimmed.toLowerCase();
  const internal = getCaseInternalName(trimmed).trim().toLowerCase();

  return cases.find((c) => {
    const name = (c.caseName || '').trim().toLowerCase();
    const tag = (c.caseTag || '').trim().toLowerCase();
    const tagBody = tag.startsWith('case:') ? tag.slice(5) : tag;
    return (
      name === normalised ||
      name === internal ||
      tag === normalised ||
      tag === `case:${normalised}` ||
      tag === `case:${internal}` ||
      tagBody === normalised ||
      tagBody === internal
    );
  });
}

/** Resolved provenance tag, or ``null`` when there is no matching case. */
export function resolveCaseTag(
  cases: CaseSummary[] | undefined | null,
  selected: string | null | undefined,
): string | null {
  const tag = findCaseSummary(cases, selected)?.caseTag?.trim();
  return tag ? tag : null;
}

/**
 * What a caller is allowed to ask provenance for.
 *
 * ``resolveCaseTag`` answers ``null`` both for All Cases and for a real case
 * whose tag has not resolved (list still loading, or a case so new it is not in
 * the list yet). Those two must not be treated the same: dropping the ``tags``
 * filter for an unresolved case lists every case in the tenant. Callers scope
 * on ``kind``, never on ``tag`` alone.
 */
export type CaseScope =
  /** Cross-case selection — an unscoped listing is intended. */
  | { kind: 'all-cases'; tag: null }
  /** One case, with the tag provenance filters must send. */
  | { kind: 'case'; tag: string }
  /** A specific case we cannot tag yet — query nothing rather than everything. */
  | { kind: 'unresolved'; tag: null };

export function resolveCaseScope(
  cases: CaseSummary[] | undefined | null,
  selected: string | null | undefined,
): CaseScope {
  if (!selected?.trim() || isAllCasesSelection(selected)) {
    return { kind: 'all-cases', tag: null };
  }
  const tag = resolveCaseTag(cases, selected);
  return tag ? { kind: 'case', tag } : { kind: 'unresolved', tag: null };
}

/**
 * The case's **S3 folder name**: its ``folder_token`` (a UUID) when it has one,
 * otherwise its display name with original casing. ``null`` when unresolvable.
 *
 * This is the one rename-safe identifier for a case (permission-service calls it
 * ``folder_segment``), and the counterpart to
 * ``resolveCaseTag``. Use the tag where the target stores lowercased
 * ``case:<folderName>`` tags written at ingest (provenance, evidence locker,
 * retrieval). Use the folder name where the *casing matters*, because the target
 * matches tags byte-exactly and legacy rows were written from the display name:
 *
 *   - timeline tags — ``CASE:<folderName>``, matched by Postgres ``&&`` overlap
 *   - entity extraction job tags — ``CASE:<folderName>``
 *
 * When the list row carries ``folderSegment``, that value is returned verbatim.
 * Recovering the folder name from ``caseTag`` is only the fallback for the
 * legacy provenance list, which does not send ``folder_segment``.
 * permission-service builds the tag as ``case:<folder_token || name>`` and
 * lowercases it (``derive_case_tag``), so the tag body equals the lowercased
 * display name *if and only if* the case is token-less. When they differ the body
 * is a ``folder_token``, and a UUID is already lowercase, so nothing was lost.
 * When they match we return ``caseName`` to recover the original casing that
 * legacy timelines are stored under — renaming a token-less case is blocked
 * server-side (409), so that name is stable. That fallback drops a legacy
 * segment whose casing or spacing differs from the display name, which is why
 * the mapped field wins whenever it is present.
 *
 * The two degenerate cases resolve correctly on their own: a token-less case
 * named like a UUID, and a token-bearing case whose display name happens to equal
 * its own token, both take the ``caseName`` branch and land on the same string.
 *
 * Never assemble a folder name from the access mode (AUTO / GATED / LEGACY). AUTO
 * cases are a mix: one adopting a folder that already holds ``case:<name>``
 * objects is deliberately created token-less, so mode does not predict the form.
 */
export function resolveCaseFolderName(
  cases: CaseSummary[] | undefined | null,
  selected: string | null | undefined,
): string | null {
  const match = findCaseSummary(cases, selected);
  if (!match) return null;
  return caseFolderNameOf(match);
}

/**
 * {@link resolveCaseFolderName} for a row the caller has already matched.
 *
 * Split out so callers that resolve a case by something other than the nav
 * selection — a scope tag, for instance — get the identical folder-segment
 * precedence instead of re-deriving it. Returns ``null`` only when the row
 * carries no usable identifier at all.
 */
export function caseFolderNameOf(summary: CaseSummary): string | null {
  // The permissioned list carries folder_segment. Use it verbatim: slicing
  // the tag lowercases a legacy name and drops interior spacing the server kept.
  const mapped = (summary.folderSegment || '').trim();
  if (mapped) return mapped;

  const name = (summary.caseName || '').trim();
  const tag = (summary.caseTag || '').trim();
  const body = (tag.toLowerCase().startsWith('case:') ? tag.slice(5) : tag).trim();
  if (!body) return name || null;

  return body === name.toLowerCase() ? name : body;
}

/** A case ``folder_token`` as it appears in an S3 key: a canonical lowercase UUID. */
const UUID_FOLDER_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/**
 * Display names for every case whose files live under a ``folder_token`` folder,
 * keyed by that token.
 *
 * A token-bearing case's documents are stored at ``<uuid>/...``, so the raw path
 * is what the evidence locker, breadcrumbs, scope chips and Sources panel would
 * otherwise render — the user sees ``c66e7de1-2eb2-419b-…`` where they expect
 * "Case 1". This map is the *only* thing allowed to change: paths, pick URLs,
 * tags and filters keep the token, because the token is what S3 and the tag
 * indexes are keyed on and a relabelled path would resolve to nothing.
 *
 * Token-less (legacy) cases are deliberately absent — their folder segment IS
 * their display name, so there is nothing to relabel and their rendering is
 * provably unchanged. Ad-hoc folders ("My Files") are likewise untouched.
 */
export function caseFolderLabels(
  cases: CaseSummary[] | undefined | null,
): Map<string, string> {
  const labels = new Map<string, string>();
  for (const summary of cases ?? []) {
    const name = (summary.caseName || '').trim();
    if (!name) continue;
    const mapped = (summary.folderSegment || '').trim();
    const tag = (summary.caseTag || '').trim().toLowerCase();
    const fromTag = (tag.startsWith('case:') ? tag.slice(5) : tag).trim();
    const body = mapped || fromTag;
    // A body equal to the lowercased name is a legacy case, not a token.
    if (!body || body.toLowerCase() === name.toLowerCase()) continue;
    if (!UUID_FOLDER_SEGMENT.test(body.toLowerCase())) continue;
    labels.set(body.toLowerCase(), name);
  }
  return labels;
}

/**
 * The human label for one path segment: the case's display name when the segment
 * is a known ``folder_token``, otherwise the segment unchanged.
 */
export function caseFolderLabel(
  segment: string,
  labels: Map<string, string> | null | undefined,
): string {
  if (!labels?.size) return segment;
  return labels.get(segment.trim().toLowerCase()) ?? segment;
}

/**
 * Relabel any ``folder_token`` segments inside a slash-delimited display path.
 *
 * For display only — never feed the result back to provenance, a pick URL, or a
 * tag filter. Returns the input unchanged (same reference) when there is nothing
 * to relabel, so callers can pass raw paths freely.
 */
export function relabelCaseFolderSegments(
  path: string,
  labels: Map<string, string> | null | undefined,
): string {
  if (!labels?.size || !path.includes('-')) return path;
  const segments = path.split('/');
  let changed = false;
  const relabelled = segments.map((segment) => {
    const label = labels.get(segment.trim().toLowerCase());
    if (label === undefined) return segment;
    changed = true;
    return label;
  });
  return changed ? relabelled.join('/') : path;
}
